/* eslint-disable no-alert */
/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import { Button, DatePicker, Modal } from 'antd';
import ReactTable from 'react-table';
import PropTypes from 'prop-types';
import moment from 'moment';
import { putAssociationValidations, putIndustryAssociations } from './graphql';
import trashCan from '../../images/trashcan.svg';
import { matchAffiliations } from './SubscriptionInfo';
import { compareAssoc } from './plans';
import { AssociationUrl } from '../../config/properties';
import { getSamDataWithoutCageAPI } from '../../graphql/organizationDetails';

const EditAssociationValidations = ({ company, onChange }) => {
  const industryAssociations = company.industryAssociations;
  const associationValidations = company.associationValidations;
  const [newAssociationValidations, setNewAssociationValidations] =
    useState(company.associationValidations ? associationValidations.map(a => ({ ...a })) : []);

  const [open, setOpen] = useState(false);

  const toggleOpen = () => setOpen(!open);

  useEffect(() => {
    fetchAssociationValidations();
  }, [associationValidations]);

  useEffect(() => {
    if (open) {
      fetchAssociationValidations();
    }
  }, [open]);

  const fetchAssociationValidations = () => {
    if (associationValidations) {
      setNewAssociationValidations(associationValidations.map(a => ({ ...a })));
    }
  }

  const handleChange = async (myAssociationValidations) => {
    const oldAssociationValidations = [...associationValidations];
    let myIndustryAssociations;
    if (!matchAffiliations(industryAssociations, myAssociationValidations)) {
      myIndustryAssociations = [];
      for (let i = 0; i < myAssociationValidations.length; i += 1) {
        myIndustryAssociations.push(myAssociationValidations[i].name);
      }
      await putIndustryAssociations(company.id, myIndustryAssociations)
    } else {
      myIndustryAssociations = company.industryAssociations;
    }
    await putAssociationValidations(company.id, myAssociationValidations)
    onChange(myIndustryAssociations, myAssociationValidations);

    // See if we need to modify subscription as a result of this change:
    const [changeAssoc, assocName] = compareAssoc(myAssociationValidations, oldAssociationValidations);
    const billsbyCustomerId = company.admins && company.admins[0].billsbyCustomerId;
    if (company.iconStatus === 'approved' && changeAssoc !== 0 && billsbyCustomerId) {
      const changeInfo = {
        company: {
          name: company.name,
          user: { billsbyCustomerId },
          email: company.email,
          pointOfContact: company.pointOfContact,
          associationValidations: myAssociationValidations,
          changeAssoc
        },
        assocName
      }
      console.log(changeAssoc > 0 ? 'Validated:' : 'Invalidated', assocName, changeInfo.company);
      try {
        await Axios.put(`${AssociationUrl}/changeSubscription`, changeInfo);
      } catch (error) {
        console.log("Error in ChangeSubscription", error);
      }
    }
  }

  return (
    <span>
      <Button type="primary" onClick={toggleOpen}>Edit Industry Affiliations</Button>
      {open && (<AssociationEditor associations={newAssociationValidations} onChange={handleChange} open={open} setOpen={setOpen} />)}
    </span >
  )
}

EditAssociationValidations.propTypes = {
  company: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired
};

const AssociationEditor = ({ associations, onChange, open, setOpen }) => {
  const [newAssociationValidations, setNewAssociationValidations] = useState(associations);
  const [change, setChange] = useState(false);
  const [affiliations, setAffiliations] = useState([]);

  const handleOk = async () => {
    if (change) {
      // Ensure that if no affiliations are selected, "None" is included
      let finalAssociationValidations = [...newAssociationValidations];
      if (finalAssociationValidations.length === 0) {
        finalAssociationValidations = [{
          name: 'None',
          status: 'unknown',
          expiration: null,
          uniqueId: null,
          pocUniqueId: null
        }];
      }
      onChange(finalAssociationValidations)
      setOpen(false);
    }
  }
  const handleCancel = () => {
    setChange(false);
    setOpen(false);
  }

  useEffect(() => {
    setNewAssociationValidations(associations);
  }, [associations]);

  useEffect(() => {
    // Fetch affiliations list from database (SPONSORS table) when modal opens
    const fetchAffiliations = async () => {
      try {
        const response = await getSamDataWithoutCageAPI("");
        if (response && response.listsSamDataWithoutCage && response.listsSamDataWithoutCage.industryAssociationsList) {
          // Extract SPONSOR_ABRV values from the industryAssociationsList
          // Handle different possible property name variations from Java serialization
          const sponsorAbbrevs = response.listsSamDataWithoutCage.industryAssociationsList
            .map(assoc =>
              // Try different property names that might come from Java backend
              assoc.sponsorAbrv || assoc.SPONSOR_ABRV || assoc.sponsor_abrv ||
              assoc.name || (typeof assoc === 'string' ? assoc : null)
            )
            .filter(abrv => abrv && abrv !== 'None' && String(abrv).trim() !== '')
            .sort();
          if (sponsorAbbrevs.length > 0) {
            // Add "None" as an option to explicitly track "no affiliation" state
            setAffiliations([...sponsorAbbrevs, 'None']);
          } else {
            // Fallback if extraction failed
            setAffiliations(['FIA', 'ICI', 'NFFS', 'NTMA', 'AFS', 'ASQ', 'ASTM', 'ICRI', 'None']);
          }
        } else {
          // Fallback if response structure is unexpected
          setAffiliations(['FIA', 'ICI', 'NFFS', 'NTMA', 'AFS', 'ASQ', 'ASTM', 'ICRI', 'None']);
        }
      } catch (error) {
        console.error('Error fetching affiliations from database:', error);
        // Fallback to hardcoded list if API fails
        setAffiliations(['FIA', 'ICI', 'NFFS', 'NTMA', 'AFS', 'ASQ', 'ASTM', 'ICRI', 'None']);
      }
    };

    if (open) {
      fetchAffiliations();
    }
  }, [open]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
    },
    {
      Header: 'Status',
      accessor: 'status',
      minWidth: 120,
      Cell: row => associationStatusSelect(row.index, row.original.status)
    },
    {
      Header: 'Expiration',
      accessor: 'expiration',
      minWidth: 150,
      Cell: row => associationDatePicker(row.index, row.original.expiration)
    },
    {
      Header: 'Action',
      minWidth: 75,
      Cell: row => associationDelete(row.index)
    }
  ];

  const associationStatusSelect = (index, cellValue) => {
    const Statuses = [
      {
        value: 'unknown',
        label: 'Unknown'
      },
      {
        value: 'current',
        label: 'Current'
      },
      {
        value: 'expired',
        label: 'Expired'
      },
      {
        value: 'grace_period',
        label: 'Grace Period'
      },
      {
        value: 'inheriting',
        label: 'Inheriting'
      },
      {
        value: 'non_member',
        label: 'Non-member'
      },
      {
        value: 'pending',
        label: 'Pending'
      },
      {
        value: 'prospect',
        label: 'Prospective'
      },
    ];

    const handleChangeStatus = event => {
      setNewAssociationValidations(newAssociationValidations.map((a, i) => {
        if (i !== index) return a;
        return { ...a, status: event.target.value };
      }))
      setChange(true);
    }
    return (
      <select
        defaultValue={cellValue}
        onChange={handleChangeStatus}
      >
        {Statuses.map(item => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
    )
  }

  const associationDatePicker = (index, value) => {
    const handleChangeExpiration = (date, dateString) => {
      setNewAssociationValidations(newAssociationValidations.map((a, i) => {
        if (i !== index) return a;
        return { ...a, expiration: dateString };
      }))
      setChange(true);
    }

    const date = value ? moment(value) : null
    return (
      <DatePicker
        defaultValue={date}
        format="YYYY-MM-DD"
        onChange={handleChangeExpiration}
      />
    )
  }

  const associationDelete = (index) => {
    const handleDelete = () => {
      const newAssociationValidations1 = newAssociationValidations.toSpliced(index, 1);
      // If all affiliations are deleted, automatically add "None"
      if (newAssociationValidations1.length === 0) {
        setNewAssociationValidations([{
          name: 'None',
          status: 'unknown',
          expiration: null,
          uniqueId: null,
          pocUniqueId: null
        }]);
      } else {
        setNewAssociationValidations(newAssociationValidations1);
      }
      setChange(true);
    }
    return (
      <Button onClick={handleDelete}>
        <img
          src={trashCan}
          alt="Delete Association"
        />
      </Button>
    )
  }

  // All affiliations from SPONSORS table: NFFS, NTMA, ICI, FIA, AFS, ASQ, ASTM, ICRI, None
  const handleNewAffiliation = (e) => {
    const selectedValue = e.target.value;
    if (!selectedValue || selectedValue === 'Add Affiliation') {
      return; // Don't add if placeholder is selected
    }

    // If "None" is selected, replace all existing affiliations with just "None"
    if (selectedValue === 'None') {
      setNewAssociationValidations([{
        name: 'None',
        status: 'unknown',
        expiration: null,
        uniqueId: null,
        pocUniqueId: null
      }]);
    } else {
      // Remove "None" if it exists when adding a real affiliation
      const filtered = newAssociationValidations.filter(a => a.name !== 'None');
      setNewAssociationValidations([...filtered, {
        name: selectedValue,
        status: 'unknown',
        expiration: null,
        uniqueId: null,
        pocUniqueId: null
      }]);
    }
    setChange(true);
  }

  return (
    <Modal
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
    >
      <h1>Edit Industry Affiliations</h1>
      <ReactTable
        data={newAssociationValidations}
        columns={columns}
        showPagination={0}
        defaultPageSize={4}
      />
      {newAssociationValidations.length < 4 && (
        <select onChange={handleNewAffiliation}>
          <option>Add Affiliation</option>
          {affiliations.map(a => (!newAssociationValidations.find((aa => aa.name === a)) && (<option value={a}>{a}</option>)))}
        </select>
      )}
    </Modal>
  );
}

AssociationEditor.propTypes = {
  associations: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
}

export default EditAssociationValidations;



// WEBPACK FOOTER //
// ./src/components/Billsby/EditAssociationValidations.jsx